import React, { useEffect, useState, useRef } from 'react';
import { CreditCardIcon, ChevronDownIcon, CheckIcon } from 'lucide-react';
export type PaymentMethod = 'all' | 'card' | 'bank_transfer' | 'ewallet' | 'bnpl' | 'crypto';
interface PaymentMethodFilterProps {
  value: PaymentMethod[];
  onChange: (methods: PaymentMethod[]) => void;
}
export function PaymentMethodFilter({
  value,
  onChange
}: PaymentMethodFilterProps) {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);
  const options: {
    value: PaymentMethod;
    label: string;
  }[] = [{
    value: 'card',
    label: 'Card'
  }, {
    value: 'bank_transfer',
    label: 'Bank transfer'
  }, {
    value: 'ewallet',
    label: 'E-wallet'
  }, {
    value: 'bnpl',
    label: 'BNPL'
  }, {
    value: 'crypto',
    label: 'Crypto'
  }];
  const toggleMethod = (method: PaymentMethod) => {
    if (value.includes(method)) {
      onChange(value.filter(m => m !== method));
    } else {
      onChange([...value, method]);
    }
  };
  const selectAll = () => {
    onChange(options.map(opt => opt.value));
  };
  const clearAll = () => {
    onChange([]);
  };
  const displayText = () => {
    if (value.length === 0) {
      return 'All payment methods';
    } else if (value.length === options.length) {
      return 'All payment methods';
    } else if (value.length === 1) {
      return options.find(opt => opt.value === value[0])?.label || 'Payment method';
    } else {
      return `${value.length} payment methods`;
    }
  };
  return <div className="relative" ref={dropdownRef} data-id="element-2674">
      <button onClick={() => setIsOpen(!isOpen)} className="flex items-center gap-2 px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50" data-id="element-2675">
        <CreditCardIcon size={16} data-id="element-2676" />
        <span data-id="element-2677">{displayText()}</span>
        <ChevronDownIcon size={16} data-id="element-2678" />
      </button>

      {isOpen && <div className="absolute left-0 mt-2 w-64 bg-white rounded-lg shadow-lg border border-gray-200 z-50" data-id="element-2679">
          {/* Select/Clear all */}
          <div className="flex items-center justify-between px-4 py-2 border-b border-gray-200" data-id="element-2680">
            <button onClick={selectAll} className="text-xs text-blue-600 hover:text-blue-700 font-medium" data-id="element-2681">
              Select all
            </button>
            <button onClick={clearAll} className="text-xs text-gray-600 hover:text-gray-700 font-medium" data-id="element-2682">
              Clear all
            </button>
          </div>

          {/* Options */}
          <div className="py-1" data-id="element-2683">
            {options.map(option => {
          const isSelected = value.includes(option.value);
          return <button key={option.value} onClick={() => toggleMethod(option.value)} className="w-full flex items-center justify-between px-4 py-2 text-sm hover:bg-gray-100" data-id="element-2684">
                  <span className={isSelected ? 'text-blue-600 font-medium' : 'text-gray-700'} data-id="element-2685">
                    {option.label}
                  </span>
                  {isSelected && <div className="w-5 h-5 bg-blue-600 rounded flex items-center justify-center" data-id="element-2686">
                      <CheckIcon size={12} className="text-white" data-id="element-2687" />
                    </div>}
                </button>;
        })}
          </div>

          {/* Apply button */}
          <div className="border-t border-gray-200 p-2" data-id="element-2688">
            <button onClick={() => setIsOpen(false)} className="w-full px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-md" data-id="element-2689">
              Apply
            </button>
          </div>
        </div>}
    </div>;
}